import React from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { Card } from "@/components/ui/card";
import { ArrowLeft, Mail, Phone, MessageCircle, Instagram, Facebook } from "lucide-react";

const PHONE = "514-247-3151";
const WHATSAPP_PHONE = "15142473151"; // for wa.me link
const EMAIL = "info@foldora.ca";

const CHANNELS = [
  {
    name: "Email", handle: EMAIL, href: `mailto:${EMAIL}`,
    icon: <Mail className="w-6 h-6" />, bg: "bg-pink-100 text-pink-600",
    label: "Send us a note",
  },
  {
    name: "Phone", handle: PHONE, href: `tel:+1${PHONE.replace(/\D/g, "")}`,
    icon: <Phone className="w-6 h-6" />, bg: "bg-emerald-100 text-emerald-700",
    label: "Call us",
  },
  {
    name: "WhatsApp", handle: `+1 ${PHONE}`, href: `https://wa.me/${WHATSAPP_PHONE}`,
    icon: <MessageCircle className="w-6 h-6" />, bg: "bg-green-100 text-green-700",
    label: "Chat now",
  },
  {
    name: "Instagram", handle: "@foldora.service",
    href: "https://www.instagram.com/foldora.service?igsh=MWlkd2o3c3Jxcnd6dQ%3D%3D&utm_source=qr",
    icon: <Instagram className="w-6 h-6" />, bg: "bg-fuchsia-100 text-fuchsia-700",
    label: "Follow us",
  },
  {
    name: "Facebook", handle: "Foldora",
    href: "https://www.facebook.com/share/1cTEdaWn9T/?mibextid=wwXIfr",
    icon: <Facebook className="w-6 h-6" />, bg: "bg-blue-100 text-blue-700",
    label: "Like our page",
  },
  {
    name: "TikTok", handle: "@foldora4",
    href: "https://www.tiktok.com/@foldora4?_r=1&_t=ZS-98dFDiKe2aL",
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.35a8.16 8.16 0 0 0 4.77 1.52V6.42a4.85 4.85 0 0 1-1.84-.27"/></svg>
    ), bg: "bg-stone-100 text-stone-900", label: "Watch on TikTok",
  },
];

export default function Contact() {
  return (
    <div data-testid="contact-page" className="min-h-screen bg-stone-50">
      <Navbar />
      <div className="max-w-5xl mx-auto px-6 py-12">
        <Link to="/" className="inline-flex items-center gap-1 text-stone-500 hover:text-pink-600 text-sm font-semibold mb-6">
          <ArrowLeft className="w-4 h-4" /> Back to home
        </Link>
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-pink-500 mb-3">Contact us</div>
        <h1 className="font-heading text-4xl lg:text-6xl font-black tracking-tighter text-stone-900 leading-[0.95]">
          We&rsquo;re here <br className="hidden sm:block" /><span className="text-pink-500">to help!</span>
        </h1>
        <p className="mt-4 text-stone-600 text-lg max-w-xl">
          Pick your favourite way to reach out — we usually reply within a couple of hours during business days.
        </p>

        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {CHANNELS.map((c) => (
            <a
              key={c.name}
              href={c.href}
              target={c.href.startsWith("http") ? "_blank" : undefined}
              rel="noreferrer"
              data-testid={`contact-${c.name.toLowerCase()}`}
              className="group"
            >
              <Card className="p-6 rounded-3xl border border-stone-200 bg-white hover:-translate-y-1 hover:shadow-md transition-all h-full">
                <div className={`w-12 h-12 rounded-2xl ${c.bg} flex items-center justify-center mb-4`}>
                  {c.icon}
                </div>
                <div className="text-xs uppercase font-bold tracking-widest text-stone-400">{c.name}</div>
                <div className="font-heading font-black text-xl text-stone-900 mt-1 break-all">{c.handle}</div>
                <div className="mt-3 inline-flex items-center gap-1 text-sm font-bold text-stone-900 border-b-2 border-stone-900 pb-0.5 group-hover:text-pink-600 group-hover:border-pink-600 transition-colors">
                  {c.label}
                </div>
              </Card>
            </a>
          ))}
        </div>

        <Card className="mt-10 p-6 rounded-3xl border-2 border-pink-200 bg-gradient-to-br from-pink-50 to-white">
          <div className="font-heading font-black text-xl text-stone-900">Business hours</div>
          <p className="text-sm text-stone-600 mt-2">
            Monday to Saturday · 9 AM – 6 PM (Eastern) · Based in Laval, Québec.
            We respond to WhatsApp and email 7 days a week.
          </p>
        </Card>
      </div>
    </div>
  );
}
